import * as React from "react";

/** The small uppercase label above a field or a group of fields. */
export const FieldLabel = ({
  children,
  required = false,
  htmlFor,
  component = "label",
}: {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  /** "span" where the label names a block rather than a control, so no empty <label> is emitted. */
  component?: "label" | "span";
}) => {
  const Component = component;
  return (
    <Component
      htmlFor={component === "label" ? htmlFor : undefined}
      className="block text-[11px] leading-[1.2] font-semibold tracking-[0.02em] text-muted-foreground uppercase"
    >
      {children}
      {required && (
        <span className="ml-0.5 text-disabled" aria-hidden>
          *
        </span>
      )}
    </Component>
  );
};

/** A validation message under a field: MUI's FormHelperText with `error`. */
export const FieldError = ({ children }: { children: React.ReactNode }) => (
  <p className="text-caption text-error">{children}</p>
);

export interface FormSectionProps {
  label: React.ReactNode;
  required?: boolean;
  /** Guidance shown under the label, before the fields. */
  hint?: React.ReactNode;
  /** A validation message shown under the fields. */
  error?: string | null;
  children: React.ReactNode;
}

/**
 * One labelled block of a form. The reference builder is a long sequence of these,
 * and it used to draw each one differently -- FormLabel here, subtitle2 plus Divider
 * there, a caption in grey.600 for the help text. One component, one rhythm.
 */
const FormSection = ({ label, required, hint, error, children }: FormSectionProps) => (
  <div className="flex flex-col gap-1.5">
    <FieldLabel required={required}>{label}</FieldLabel>
    {hint && <div className="text-caption leading-[1.5] text-muted-foreground">{hint}</div>}
    {children}
    {error && <FieldError>{error}</FieldError>}
  </div>
);

export default FormSection;
